/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn } from '@fluentui/react/lib/DetailsList';
import orderBy from 'lodash.orderby';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { CluProjectsTable } from 'src/clu/appShell/cluProjectsListPage/components/CluProjectsTable';
import { CluProjectsTableCommandBar } from 'src/clu/appShell/cluProjectsListPage/components/CluProjectsTableCommandBar';
import { CluProjectType } from 'src/clu/core/services/projects/cluProjectSwaggers';
import { CluProjectStore } from 'src/clu/core/stores/CluProjectStore';
import { getCluProjectImportDataFromFileContent } from 'src/clu/modules/handlers/projects/cluProjectMappers';
import { CluProjectCreationModal, CluProjectDto } from 'src/clu/verticals/cluProjectCreationModal/CluProjectCreationModal';
import { CopyProjectModal } from 'src/core/components/CopyProjectModal';
import { getCopyProjectToastData } from 'src/core/components/copyProjectToastData';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { FeatureUnavailableInRegion, ResourceUnSupportedErrorType } from 'src/core/components/FeatureUnavailableInRegion';
import { ImportModal } from 'src/core/components/ImportModal';
import { LuisText } from 'src/core/components/LuisText';
import { Page } from 'src/core/components/Page';
import { TablePagination } from 'src/core/components/TablePagination';
import { SortKeyType } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { usePagination } from 'src/core/hooks/usePagination';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useSelection } from 'src/core/hooks/useSelection';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { useZoomLevel } from 'src/core/hooks/useZoomLevel';
import { fileService } from 'src/core/services/api/fileService';
import {
    logButtonClick,
    logButtonSuccessfulClick,
    logMultiLingualInProjectCreation,
    logProjectClicked,
    logSelectLanguageInProjectCreation
} from 'src/core/services/telemetry/eventsNamesTemplate';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { isCluResource } from 'src/core/utils/csAccountUtils';
import { FormValidators } from 'src/core/utils/formValidators';
import { globalMessages } from 'src/core/utils/globalMessages';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { routeTable } from 'src/core/utils/routeTable';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { createKeyedStringNormalizer } from 'src/core/utils/strings';
import { SignedOutEmptyCustomization } from 'src/languageStudio/appShell/SignedOutEmptyCustomization';
import { EmptyProjectsListView } from 'src/modules/projects/EmptyProjectsListView';

type CluProjectsListPageProps = { serviceType: CluProjectType };

const messages = defineMessages({
    importModalTitle: { id: 'clu.cluProjectsListPage.importModalTitle', defaultMessage: 'Import project' },
    exportRunningToast: { id: 'clu.cluProjectsListPage.exportRunningToast', defaultMessage: 'Exporting project ...' },
    deletingProjectTitle: { id: 'clu.cluProjectsListPage.deletingProjectTitle', defaultMessage: 'Delete selected project(s)' },
    exportCompletedToast: { id: 'clu.cluProjectsListPage.exportCompletedToast', defaultMessage: 'Export completed successfully' },
    failedDeletingProjects: { id: 'clu.cluProjectsListPage.failedDeletingProjects', defaultMessage: '{failedCount} projects failed.' },
    orchestrationPageTitle: {
        id: 'clu.cluProjectsListPage.orchestrationPageTitle',
        defaultMessage: 'Select the project you want to work with'
    },
    conversationPageTitle: {
        id: 'clu.cluProjectsListPage.conversationPageTitle',
        defaultMessage: 'Select the project you want to work with'
    },
    subtitle: {
        id: 'clu.cluProjectsListPage.subtitle',
        defaultMessage:
            'A conversational language understanding project contains both labeled data as well as the models that are trained from that data. The labeled data consists of utterances that you annotate with entity labels and categorize into intents.'
    },
    deletingProjectBody: {
        id: 'clu.cluProjectsListPage.deletingProjectBody',
        defaultMessage: 'Are you sure you want to delete the selected project(s)?'
    },
    noResourceSelected: {
        id: 'clu.cluProjectsListPage.noResourceSelected',
        defaultMessage: 'Please select subscription and azure resource to list your projects.'
    },
    projectNameAlreadyExistsWarningMessage: {
        id: 'clu.cluProjectsListPage.projectNameAlreadyExistsWarningMessage',
        defaultMessage: 'Project {projectName} already exists, Importing this file will overwrite the existing project.'
    },
    nameErrorMessage: {
        id: 'clu.cluProjectsListPage.nameErrorMessage',
        defaultMessage: 'Invalid characters: You can only use "-", "_", "." and alphanumeric characters.'
    }
});

const { styleDiv } = getStylistV2('CluProjectsListPage');
const ProjectsTableWindow = styleDiv('ProjectsTableWindow', { height: '100%', overflowY: 'auto' });
const defaultModalsState = {
    isCopyProjectModalHidden: true,
    isImportProjectModalHidden: true,
    isCreateProjectModalHidden: true,
    isDeleteProjectModalHidden: true
};

export const CluProjectsListPage = observer((props: CluProjectsListPageProps) => {
    const { serviceType } = props;
    const history = useHistory();
    const { localize } = useLocalization();
    const { serviceName } = getPortalFromUrl();
    const { isAccessibleMode } = useZoomLevel();
    const [query, setQuery] = React.useState('');
    const { dispatcher, rootStore } = useGlobalState();
    const { selection, selectedItems } = useSelection<CluProjectStore>();

    const [{ projectNameToImport, importProjectWarningMessage }, setImportInfo] = React.useState({
        projectNameToImport: '',
        importProjectWarningMessage: ''
    });

    const [sortKey, setSortKey] = React.useState<SortKeyType<CluProjectStore>>({ key: 'modifiedDate', order: 'desc' });
    const [modalsState, setModalsState] = useUpdateState(defaultModalsState);

    const { isSignedIn } = rootStore.authStore;
    const { cluConversationCultureStoreHouse, cluOrchestrationCultureStoreHouse, cluProjectStoreHouse } = rootStore;
    const { csAccountStore } = rootStore.azureStore.session;

    const defaultOperationsTracker = useProgressTracker({
        onComplete: () => {
            setModalsState(defaultModalsState);
            selection.setAllSelected(false);
        }
    });

    const importTracker = useProgressTracker({
        onComplete: (projectName: string) => {
            setModalsState(defaultModalsState);
            selection.setAllSelected(false);
            openProject(projectName);
        }
    });

    React.useEffect(() => {
        if (csAccountStore?.isCluCompatibleResource && history.location.state && (history.location.state as boolean)) {
            setModalsState({ isCreateProjectModalHidden: false });
        }
    }, []);

    React.useEffect(() => {
        if (
            rootStore.serviceKey.value &&
            rootStore.azureStore.session.csAccountStore?.kind === 'TextAnalytics' &&
            LuisConstants.Clu.PrivatePreviewAllowedRegions.some(r => r === csAccountStore.region)
        ) {
            dispatcher.dispatch('loadCluProjects', { toastData: {} });
        }
        // The account id is added to the dependency list as in AAD authentication the service key has same value regardless of changing the resource.
    }, [rootStore.serviceKey.value, rootStore.azureStore.session.csAccountStore?.id, rootStore.azureStore.session.csAccountStore?.kind]);

    const filteredProjects = orderBy(
        substringSearchStrategy(
            cluProjectStoreHouse.items.filter(p => p.projectType === serviceType),
            query,
            'name'
        ),
        [createKeyedStringNormalizer(sortKey.key)],
        [sortKey.order]
    );

    const { currentPage, setCurrentPage, pageItems, pageCount } = usePagination(filteredProjects, 10);

    const canCreateProject = csAccountStore?.isCluCompatibleResource;

    const handleColumnClick = (c: IColumn) => {
        setSortKey({ key: c.key as keyof CluProjectStore, order: c.isSortedDescending ? 'asc' : 'desc' });
        selection.setAllSelected(false);
    };

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
        selection.setAllSelected(false);
    };

    const openProject = (projectId: string) => {
        logProjectClicked(serviceName, 'ProjectList', projectId);
        history.push(routeTable[serviceType === 'Conversation' ? 'clu' : 'orchestration'].projectPage(projectId));
    };

    const getCultureName = (code: string) =>
        (serviceType === 'Conversation' ? cluConversationCultureStoreHouse : cluOrchestrationCultureStoreHouse).items.find(
            item => item.code === code
        )?.name ?? '';

    const onProjectCreated = (dto: CluProjectDto) => {
        logSelectLanguageInProjectCreation(serviceName, getCultureName(dto.culture), dto.name);

        if (dto.isMultiLingual) {
            logMultiLingualInProjectCreation(serviceName, dto.name);
        }
        setModalsState({ isCreateProjectModalHidden: true });
        openProject(dto.name);
    };

    const handleDeleteProject = () => {
        dispatcher.dispatch('deleteCluProjects', {
            toastData: {},
            tracker: defaultOperationsTracker,
            projectNames: selectedItems.map(item => item.name),
            generateMultiFailureMessage: (failedCount: number) => localize(messages.failedDeletingProjects, { failedCount })
        });

        selectedItems.forEach(item => logButtonSuccessfulClick(serviceName, 'ProjectList', 'Delete', null, item.name));
    };

    const handleImportProject = (file: File) => {
        dispatcher.dispatch('importCluProject', { file, newProjectName: projectNameToImport, tracker: importTracker, toastData: {} });
        logButtonSuccessfulClick(serviceName, 'ProjectList', 'Import', null, projectNameToImport);
    };

    const handleExportProject = () => {
        dispatcher.dispatch('exportCluProject', {
            projectName: selectedItems[0].name,
            tracker: defaultOperationsTracker,
            toastData: {
                running: { title: localize(messages.exportRunningToast) },
                completed: { title: localize(messages.exportCompletedToast) }
            }
        });
        logButtonClick(serviceName, 'ProjectList', 'Export', null, selectedItems[0].name);
    };

    const onCopyProjectClicked = (allowOverwrite: boolean, resourceSelected: AzureCsAccountStore, selectedSubscriptionId: string) => {
        dispatcher.dispatch('copyCluProject', {
            allowOverwrite,
            targetResource: resourceSelected,
            tracker: defaultOperationsTracker,
            projectName: selectedItems?.[0].name,
            projectKind: selectedItems?.[0].projectType,
            toastData: getCopyProjectToastData({
                localize,
                dispatcher,
                selectedSubscriptionId,
                resourceName: resourceSelected.name,
                projectName: selectedItems?.[0].name,
                selectedResourceId: resourceSelected.id
            })
        });

        setModalsState({ isCopyProjectModalHidden: true });
    };
    const validateProjectNameWhileImport = async (file: File, projectName: string) => {
        let name: string = projectName;

        if (!name && file) {
            const fileContent = await fileService.readFromDisk(file);
            name = getCluProjectImportDataFromFileContent(fileContent).projectName;
        }

        if (cluProjectStoreHouse.items.some(p => p.name === name)) {
            setImportInfo({
                projectNameToImport: name,
                importProjectWarningMessage: localize(messages.projectNameAlreadyExistsWarningMessage, { projectName: name })
            });
        } else {
            setImportInfo({ projectNameToImport: name, importProjectWarningMessage: '' });
        }
    };

    const commandBar = (
        <CluProjectsTableCommandBar
            query={query}
            onQueryChange={setQuery}
            serviceType={serviceType}
            selectedItems={selectedItems}
            canCreateProject={canCreateProject}
            onExportProject={handleExportProject}
            isCreateProjectModalHidden={modalsState.isCreateProjectModalHidden}
            onCopyProject={() => {
                setModalsState({ isCopyProjectModalHidden: false });
                logButtonClick(serviceName, 'ProjectList', 'Copy');
            }}
            onImportProject={() => {
                setModalsState({ isImportProjectModalHidden: false });
                logButtonClick(serviceName, 'ProjectList', 'Import');
            }}
            onCreateProject={() => {
                setModalsState({ isCreateProjectModalHidden: false });
                logButtonClick(serviceName, 'ProjectList', 'CreateProject');
            }}
            onDeleteProjects={() => {
                setModalsState({ isDeleteProjectModalHidden: false });
                logButtonClick(serviceName, 'ProjectList', 'Delete');
            }}
        />
    );

    if (!isSignedIn) {
        return (
            <Page
                size="container-fluid"
                pageName="ProjectsListPage"
                title={localize(serviceType === 'Conversation' ? messages.conversationPageTitle : messages.orchestrationPageTitle)}>
                <EditableTitle
                    title={localize(serviceType === 'Conversation' ? messages.conversationPageTitle : messages.orchestrationPageTitle)}
                    subtitle={localize(messages.subtitle)}
                    dataAutomationId={serviceType === 'Conversation' ? 'cluProjectsPageTitle' : 'orchestrationProjectsPageTitle'}
                />

                {commandBar}

                <SignedOutEmptyCustomization
                    service={serviceType === 'Conversation' ? 'conversation' : 'orchestration'}
                    serviceDocumentationLink={
                        serviceType === 'Conversation' ? 'https://aka.ms/language/clu' : 'https://aka.ms/orchestration-overview'
                    }
                />
            </Page>
        );
    }

    const errorType: ResourceUnSupportedErrorType = !csAccountStore?.isTextAnalyticsResourceKind
        ? 'unsupportedResource'
        : 'unsupportedRegion';

    return (
        <Page
            size="container-fluid"
            pageName="ProjectsListPage"
            title={localize(serviceType === 'Conversation' ? messages.conversationPageTitle : messages.orchestrationPageTitle)}>
            <EditableTitle
                title={localize(serviceType === 'Conversation' ? messages.conversationPageTitle : messages.orchestrationPageTitle)}
                subtitle={localize(messages.subtitle)}
                dataAutomationId={serviceType === 'Conversation' ? 'cluProjectsPageTitle' : 'orchestrationProjectsPageTitle'}
            />

            {csAccountStore?.isCluCompatibleResource ? (
                <>
                    {commandBar}

                    {cluProjectStoreHouse.tracker.isRunning || pageItems.length ? (
                        <ProjectsTableWindow data-is-scrollable={!isAccessibleMode}>
                            <CluProjectsTable
                                sortKey={sortKey}
                                items={pageItems}
                                selection={selection}
                                isLoading={cluProjectStoreHouse.tracker.isRunning}
                                areItemsFiltered={Boolean(query)}
                                overallItemsCount={filteredProjects.length}
                                onProjectClick={openProject}
                                announcedMessage={localize(globalMessages.invalidRegion, {
                                    featureName: 'Conversational language understanding'
                                })}
                                onSortColumn={handleColumnClick}
                            />
                        </ProjectsTableWindow>
                    ) : (
                        <EmptyProjectsListView message={!csAccountStore ? localize(messages.noResourceSelected) : undefined} />
                    )}
                </>
            ) : (
                <FeatureUnavailableInRegion
                    featureName="Conversational language understanding"
                    docsLink="https://aka.ms/clu-regions"
                    errorType={errorType}
                />
            )}

            <TablePagination
                pageCount={pageCount}
                currentPage={currentPage}
                onPageChange={p => handlePageChange(p.selected)}
                containerStyles={{ root: { marginBottom: '0 !important' } }}
            />

            <CluProjectCreationModal
                serviceType={serviceType}
                onSubmit={onProjectCreated}
                hidden={modalsState.isCreateProjectModalHidden}
                onDismiss={() => setModalsState({ isCreateProjectModalHidden: true })}
            />

            <CopyProjectModal
                hidden={modalsState.isCopyProjectModalHidden}
                onDismiss={() => setModalsState({ isCopyProjectModalHidden: true })}
                serviceTarget="TextAnalytics"
                strategies={{
                    resource: isCluResource
                }}
                onCopyProjectClicked={onCopyProjectClicked}
                isSupportBYOS={false}
            />

            <DeletePromptModal
                onDelete={handleDeleteProject}
                disabled={defaultOperationsTracker.isRunning}
                hidden={modalsState.isDeleteProjectModalHidden}
                onDismiss={() => setModalsState({ isDeleteProjectModalHidden: true })}
                dialogContentProps={{ title: localize(messages.deletingProjectTitle) }}>
                <LuisText>{localize(messages.deletingProjectBody)}</LuisText>
            </DeletePromptModal>

            <ImportModal
                fileType="json"
                disabled={importTracker.isRunning}
                warningMessage={importProjectWarningMessage}
                validateName={validateProjectNameWhileImport}
                hidden={modalsState.isImportProjectModalHidden}
                onSubmit={({ file }) => handleImportProject(file)}
                dialogContentProps={{ title: localize(messages.importModalTitle) }}
                onDismiss={() => setModalsState({ isImportProjectModalHidden: true })}
                nameValidators={[
                    FormValidators.createLengthValidator(LuisConstants.Clu.maxNamesCharLength),
                    FormValidators.createBlacklistValidator({
                        mode: 'include',
                        errorMessage: messages.nameErrorMessage,
                        characterList: LuisConstants.ResourceLimits.AllowedNameReges
                    })
                ]}
            />
        </Page>
    );
});
